import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useLoading } from "../../../common/controls/LoadingContext";

export interface FormErrors {
    name?: string;
    email?: string;
    code?: string;
    intro?: string;
    desc?: string;
    bookTitle?: string;
    bookLink?: string;
    profile?: string;
    keyword?: string;
    instarLink?: string;
    twitterLink?: string;
    blogLink?: string;
    brunchLink?: string;
    snsExtra?: string;
}

const isValidUrl = (url: string): boolean => {
    const regex = /^(ftp|http|https):\/\/[^\s/$.?#].[^\s]*$/;
    return regex.test(url);
};

// SNS 링크는 아이디만 입력하는 것을 방지하기 위해 https:// 형식을 강제
export const isValidHttpsUrl = (url: string): boolean => {
    const regex = /^https:\/\/[^\s/$.?#].[^\s]*$/;
    return regex.test(url);
};

/**
 * /addAuthor(작가 등록) 페이지의 상태·검증·제출·뒤로가기 가드를 전담하는 훅.
 * RegisterAuthorPage_new.tsx는 이 훅의 결과를 받아 섹션 컴포넌트에 나눠주기만 한다.
 */
export const useAuthorRegisterForm = () => {
    const navigate = useNavigate();
    const { showLoading, hideLoading } = useLoading();

    // 이름 / 실명인증
    const [authorName, setAuthorName] = useState<string>("");
    const [isRealNameVerified, setIsRealNameVerified] = useState<boolean>(false);
    const [isRealNameStatusLoading, setIsRealNameStatusLoading] = useState<boolean>(true);
    const [isRealNameVerifiedFromServer, setIsRealNameVerifiedFromServer] =
        useState<boolean>(false);
    const [realNameAuthError, setRealNameAuthError] = useState<string>("");
    const realNameInputRef = useRef<HTMLInputElement>(null);

    // 이메일 인증
    const [authorEmail, setAuthorEmail] = useState<string>(localStorage.getItem("email") ?? "");
    const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);
    const [emailVerificationCode, setEmailVerificationCode] = useState<string>("");
    const [eamilCode, setEmailCode] = useState("");
    const [emailVerificationButtonClick, setEmailVerificationButtonClick] = useState(false);
    const [emailMsg, setEmailMsg] = useState<string>("");
    const [isEmailCheck, setIsEmailCheck] = useState<boolean>(false);
    const [emailCheck, setEmailCheck] = useState<string>("");
    const emailRef = useRef<HTMLInputElement>(null);
    const codeRef = useRef<HTMLInputElement>(null);

    // SNS 링크
    const [instarLink, setInstarLink] = useState<string>("");
    const [twitterLink, setTwitterLink] = useState<string>("");
    const [brunchLink, setBrunchLink] = useState<string>("");
    const [blogLink, setBlogLink] = useState<string>("");
    const [snsLinks, setSnsLinks] = useState<string[]>([]);
    const instarRef = useRef<HTMLInputElement>(null);
    const twitterRef = useRef<HTMLInputElement>(null);
    const blogRef = useRef<HTMLInputElement>(null);
    const brunchRef = useRef<HTMLInputElement>(null);

    const handleLinkAdd = () => {
        if (snsLinks.length < 2) {
            setSnsLinks([...snsLinks, ""]);
        }
    };

    const handleSnsLinkChange = (index: number, value: string) => {
        const newLinks = [...snsLinks];
        newLinks[index] = value;
        setSnsLinks(newLinks);
    };

    // 소개 / 설명
    const [authorIntro, setAuthorIntro] = useState<string>("");
    const [authorDescription, setAuthorDescription] = useState<string>("");
    const introRef = useRef<HTMLTextAreaElement>(null);
    const descRef = useRef<HTMLTextAreaElement>(null);

    // 대표작
    const [representativeBookTitle, setRepresentativeBookTitle] = useState<string>("");
    const [representativeBookLink, setRepresentativeBookLink] = useState<string>("");
    const titleRef = useRef<HTMLInputElement>(null);
    const linkRef = useRef<HTMLInputElement>(null);

    // 키워드
    const keywords = [
        "재미와 감동을 주는",
        "전문 식견이 담긴",
        "소재가 신선한",
        "객관적 근거를 제시하는",
        "경험을 바탕으로 한",
        "극적 긴장감을 주는",
        "감수성이 돋보이는",
        "나만의 생각이 담긴",
        "표현력이 자유로운",
        "상상력이 돋보이는",
        "주장이 명확한",
        "논리적인 구조를 갖춘",
    ];
    const [selectedKeywords, setSelectedKeywords] = useState<string | null>("경험을 바탕으로 한");
    const keywordRef = useRef<HTMLDivElement | null>(null);

    const toggleKeyword = (keyword: string) => {
        if (selectedKeywords === keyword) {
            setSelectedKeywords(null);
        } else {
            setSelectedKeywords(keyword);
        }
    };

    // 프로필 사진
    const [image, setImage] = useState<string | null>(null);
    const [profilePreview, setProfilePreview] = useState<string | undefined>(undefined);
    const [showImageCrop, setShowImageCrop] = useState(false);
    const fileInputProfileRef = useRef<HTMLInputElement | null>(null);
    const profileRef = useRef<HTMLDivElement>(null);

    const openImageCrop = () => setShowImageCrop(true);
    const closeImageCrop = () => setShowImageCrop(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            setImage(reader.result as string);
            openImageCrop();
        };
        reader.readAsDataURL(file);
        e.target.value = "";
    };

    const handleCrop = (cropData: string) => {
        setProfilePreview(cropData);
        closeImageCrop();
    };

    // 추가 질문 (선택 항목)
    const [showExtraFields, setShowExtraFields] = useState<boolean>(false);
    const [mbti, setMbti] = useState<string>("");
    const [writingGenres, setWritingGenres] = useState<string[]>([]);
    const [inspireSource, setInspireSource] = useState<string>("");
    const [favoriteWorkTitle, setFavoriteWorkTitle] = useState<string>("");
    const [favoriteWorkReason, setFavoriteWorkReason] = useState<string>("");
    const [memorableEpisode, setMemorableEpisode] = useState<string>("");
    const [futureWritingPlan, setFutureWritingPlan] = useState<string>("");
    const [happiestWritingMoment, setHappiestWritingMoment] = useState<string>("");

    // 알림/모달 플로우
    const [showMidAlert, setShowMidAlert] = useState<boolean>(false);
    const [showBackAlert, setShowBackAlert] = useState<boolean>(false);
    const [showStep1BackAlert, setShowStep1BackAlert] = useState<boolean>(false);
    const [showAlert, setShowAlert] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [authorRegSubmitButtonClick, setAuthorRegSubmitButtonClick] = useState(false);

    const [errors, setErrors] = useState<FormErrors>({});

    // localStorage에서 이메일 동기화 (Header에서 늦게 설정되는 경우 대비)
    useEffect(() => {
        const storedEmail = localStorage.getItem("email");
        if (storedEmail && !authorEmail) {
            setAuthorEmail(storedEmail);
        }
    }, [authorEmail]);

    // 1단계 최초 진입 시 히스토리 상태 저장
    useEffect(() => {
        window.history.pushState({ step: 1 }, "", window.location.href);
    }, []);

    // 2단계 진입 시 현재 히스토리 상태 저장
    useEffect(() => {
        if (showExtraFields && !isSubmitted) {
            window.history.pushState({ step: 2 }, "", window.location.href);
        }
    }, [showExtraFields, isSubmitted]);

    // 뒤로가기 차단 및 알림 로직
    useEffect(() => {
        if (isSubmitted) return;

        const handlePopState = () => {
            if (isSubmitted) return;

            if (showExtraFields) {
                // 2단계 뒤로가기
                window.history.pushState({ step: 2 }, "", window.location.href);
                setShowBackAlert(true);
            } else {
                // 1단계 뒤로가기
                window.history.pushState({ step: 1 }, "", window.location.href);
                setShowStep1BackAlert(true);
            }
        };

        window.addEventListener("popstate", handlePopState);
        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, [showExtraFields, isSubmitted]);

    // 실명인증 서버 상태 조회
    useEffect(() => {
        const jwtToken = localStorage.getItem("jwtToken");

        if (!jwtToken) {
            setIsRealNameStatusLoading(false);
            return;
        }

        axios
            .post("/api/arth/reg/status", null, {
                headers: {
                    Authorization: `Bearer ${jwtToken}`,
                },
            })
            .then((response) => {
                const verifiedName = response.data?.verifiedName;
                const realNameVerified =
                    response.data?.realNameVerified === true &&
                    typeof verifiedName === "string" &&
                    verifiedName.trim().length > 0;

                if (realNameVerified) {
                    setAuthorName(verifiedName);
                    setIsRealNameVerified(true);
                    setIsRealNameVerifiedFromServer(true);
                    setRealNameAuthError("");
                }
            })
            .catch((error) => {
                console.error("Error fetching real-name verification status:", error);
            })
            .finally(() => {
                setIsRealNameStatusLoading(false);
            });
    }, []);

    // 실명인증 에러가 사라지면 이름 필드 에러도 정리
    useEffect(() => {
        if (isRealNameVerified) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.name;
                return newErrors;
            });
        }
    }, [isRealNameVerified]);

    const scrollToField = (el: HTMLElement | null) => {
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => {
            if ("focus" in el && typeof (el as any).focus === "function") {
                (el as any).focus();
            }
        }, 300);
    };

    const handleEmailVerification = () => {
        const jwtToken = localStorage.getItem("jwtToken");
        setEmailVerificationButtonClick(true);

        const params = new URLSearchParams();
        params.append("authorEmail", authorEmail);

        axios
            .post("/api/arth/emailCheck", params, {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Authorization: `Bearer ${jwtToken}`,
                },
            })
            .then((response) => {
                const verificationCode = response.data.code;
                if (verificationCode.length > 4) {
                    alert("이미 작가로 등록된 이메일 입니다. ");
                } else {
                    setEmailMsg("작성하신 이메일로 인증코드가 발송되었습니다.");
                    setEmailCode(verificationCode);
                    setIsEmailVerified(true);
                }
            })
            .catch((error) => {
                console.error("Error sending verification code:", error);
            })
            .finally(() => {
                setEmailVerificationButtonClick(false);
            });
    };

    const handleEmailVerificationCheck = () => {
        if (emailVerificationCode === eamilCode) {
            setIsEmailCheck(true);
            setEmailCheck("*인증이 완료되었습니다. ");
        } else {
            setIsEmailCheck(false);
            setEmailCheck("*정확한 코드를 입력해 주세요");
        }
    };

    const validateCheck = (): boolean => {
        const newErrors: FormErrors = {};

        if (!authorName) {
            newErrors.name = "올바른 실명을 입력해 주세요.";
            scrollToField(realNameInputRef.current);
        } else if (!isRealNameVerified) {
            newErrors.name = realNameAuthError || "실명 인증을 완료해 주세요.";
            scrollToField(realNameInputRef.current);
        } else if (!authorEmail) {
            newErrors.email = "이메일을 입력해 주세요.";
            scrollToField(emailRef.current);
        } else if (!isEmailCheck) {
            if (!isEmailVerified) {
                newErrors.email = "이메일 인증을 완료해 주세요.";
                scrollToField(emailRef.current);
            } else {
                newErrors.code = "인증번호를 확인해 주세요.";
                scrollToField(codeRef.current);
            }
        } else if (!emailVerificationCode) {
            newErrors.code = "인증 코드를 입력해 주세요.";
            scrollToField(codeRef.current);
        } else if (instarLink && !isValidHttpsUrl(instarLink)) {
            newErrors.instarLink = "https:// 형태로 입력해 주세요.";
            scrollToField(instarRef.current);
        } else if (twitterLink && !isValidHttpsUrl(twitterLink)) {
            newErrors.twitterLink = "https:// 형태로 입력해 주세요.";
            scrollToField(twitterRef.current);
        } else if (blogLink && !isValidHttpsUrl(blogLink)) {
            newErrors.blogLink = "https:// 형태로 입력해 주세요.";
            scrollToField(blogRef.current);
        } else if (brunchLink && !isValidHttpsUrl(brunchLink)) {
            newErrors.brunchLink = "https:// 형태로 입력해 주세요.";
            scrollToField(brunchRef.current);
        } else if (snsLinks.some((link) => link && !isValidHttpsUrl(link))) {
            newErrors.snsExtra = "https:// 형태로 입력해 주세요.";
        } else if (authorIntro.length < 50) {
            newErrors.intro = "최소 50자 이상 작성해 주세요.";
            scrollToField(introRef.current);
        } else if (authorDescription.length < 200) {
            newErrors.desc = "최소 200자 이상 작성해 주세요.";
            scrollToField(descRef.current);
        } else if (!representativeBookTitle) {
            newErrors.bookTitle = "대표작을 입력해 주세요.";
            scrollToField(titleRef.current);
        } else if (representativeBookTitle.length > 20) {
            newErrors.bookTitle = "최대 20자까지 입력 가능합니다.";
            scrollToField(titleRef.current);
        } else if (!representativeBookLink) {
            newErrors.bookLink = "대표작 링크를 입력해 주세요.";
            scrollToField(linkRef.current);
        } else if (!isValidUrl(representativeBookLink)) {
            newErrors.bookLink = "http 링크 형식에 맞게 입력해 주세요.";
            scrollToField(linkRef.current);
        } else if (!selectedKeywords) {
            newErrors.keyword = "키워드를 선택해 주세요.";
            scrollToField(keywordRef.current);
        } else if (!profilePreview) {
            newErrors.profile = "*필수 항목입니다.";
            scrollToField(profileRef.current);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const base64ToBlob = (base64: string, contentType: string): Blob => {
        const byteCharacters = atob(base64.split(",")[1]);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
        return new Blob(byteArrays, { type: contentType });
    };

    const blobToFile = (blob: Blob, fileName: string): File => {
        return new File([blob], fileName, { type: blob.type });
    };

    const doSubmit = async () => {
        const jwtToken = localStorage.getItem("jwtToken");
        setShowMidAlert(false);

        const formData = new FormData();
        formData.append("authorEmail", authorEmail);
        formData.append("authorName", authorName);

        if (instarLink) formData.append("instagramLink", instarLink);
        if (twitterLink) formData.append("xLink", twitterLink);
        if (blogLink) formData.append("naverLink", blogLink);
        if (brunchLink) formData.append("brunchLink", brunchLink);

        snsLinks.forEach((value, index) => {
            if (value) formData.append(`snsLink${index + 1}`, value);
        });

        formData.append("authorIntro", authorIntro);
        formData.append("authorDescription", authorDescription);
        formData.append("keyword1", selectedKeywords || "");

        formData.append("representativeBookTitle", representativeBookTitle);
        formData.append("representativeBookLink", representativeBookLink);

        // 추가 선택 항목
        if (mbti) formData.append("keyword2", mbti);
        if (writingGenres.length > 0) formData.append("keyword3", writingGenres.join(","));
        if (inspireSource) formData.append("inspirationSource", inspireSource);
        if (favoriteWorkTitle || favoriteWorkReason) {
            formData.append("favoriteWorkAndQuote", `${favoriteWorkTitle}: ${favoriteWorkReason}`);
        }
        if (memorableEpisode) formData.append("behindCreationEpisode", memorableEpisode);
        if (happiestWritingMoment) formData.append("happiestWritingMoment", happiestWritingMoment);

        if (profilePreview) {
            const blob = base64ToBlob(profilePreview, "image/png");
            const file = blobToFile(blob, "profile.png");
            formData.append("profilePhoto", file);
        }

        try {
            setAuthorRegSubmitButtonClick(true);
            showLoading();
            const response = await axios.post("/api/arth/reg", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${jwtToken}`,
                },
            });

            if (response.status === 200) {
                setIsSubmitted(true);
                setShowAlert(true);
            }
        } catch (error) {
            const errorContent = error as any;
            if (errorContent.response && errorContent.response.data) {
                const errorData = errorContent.response.data;
                if (typeof errorData === "string") {
                    alert(errorData);
                } else if (typeof errorData === "object") {
                    alert(errorData.message || JSON.stringify(errorData));
                } else {
                    alert("알 수 없는 에러가 발생했습니다.");
                }
            } else {
                alert("알 수 없는 에러가 발생했습니다.");
            }
        } finally {
            hideLoading();
            setAuthorRegSubmitButtonClick(false);
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const jwtToken = localStorage.getItem("jwtToken");
        if (!jwtToken) {
            alert("로그인 상태를 확인해주세요.");
            return;
        }

        if (!validateCheck()) {
            return;
        }

        if (showExtraFields) {
            // 추가 질문이 이미 열려있으면 바로 제출
            doSubmit();
        } else {
            // 중간 알림 모달 표시
            setShowMidAlert(true);
        }
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const extraFieldsProps = {
        mbti,
        setMbti,
        writingGenres,
        setWritingGenres,
        inspireSource,
        setInspireSource,
        favoriteWorkTitle,
        setFavoriteWorkTitle,
        favoriteWorkReason,
        setFavoriteWorkReason,
        memorableEpisode,
        setMemorableEpisode,
        futureWritingPlan,
        setFutureWritingPlan,
        happiestWritingMoment,
        setHappiestWritingMoment,
    };

    return {
        navigate,
        errors,

        // 이름
        authorName,
        setAuthorName,
        isRealNameVerified,
        setIsRealNameVerified,
        isRealNameStatusLoading,
        isRealNameVerifiedFromServer,
        realNameAuthError,
        setRealNameAuthError,
        realNameInputRef,

        // 이메일
        authorEmail,
        setAuthorEmail,
        isEmailVerified,
        emailVerificationCode,
        setEmailVerificationCode,
        emailVerificationButtonClick,
        emailMsg,
        isEmailCheck,
        emailCheck,
        emailRef,
        codeRef,
        handleEmailVerification,
        handleEmailVerificationCheck,

        // SNS
        instarLink,
        setInstarLink,
        twitterLink,
        setTwitterLink,
        blogLink,
        setBlogLink,
        brunchLink,
        setBrunchLink,
        snsLinks,
        instarRef,
        twitterRef,
        blogRef,
        brunchRef,
        handleLinkAdd,
        handleSnsLinkChange,

        // 소개/설명
        authorIntro,
        setAuthorIntro,
        authorDescription,
        setAuthorDescription,
        introRef,
        descRef,

        // 대표작
        representativeBookTitle,
        setRepresentativeBookTitle,
        representativeBookLink,
        setRepresentativeBookLink,
        titleRef,
        linkRef,

        // 키워드
        keywords,
        selectedKeywords,
        toggleKeyword,
        keywordRef,

        // 프로필 사진
        image,
        profilePreview,
        showImageCrop,
        fileInputProfileRef,
        profileRef,
        handleFileChange,
        handleCrop,
        closeImageCrop,

        // 추가 질문
        showExtraFields,
        setShowExtraFields,
        extraFieldsProps,

        // 알림/모달 플로우
        showMidAlert,
        setShowMidAlert,
        showBackAlert,
        setShowBackAlert,
        showStep1BackAlert,
        setShowStep1BackAlert,
        showAlert,
        setShowAlert,
        isSubmitted,
        setIsSubmitted,
        authorRegSubmitButtonClick,

        // 액션
        handleSubmit,
        doSubmit,
        scrollToTop,
    };
};

export default useAuthorRegisterForm;
